import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { LoadingState } from "@/components/base/LoadingState";
import { Modal } from "@/components/base/Modal";
import { SettingsNav } from "@/pages/settings/components/SettingsNav";
import { AdminOnly } from "@/components/feature/AdminOnly";
import { TimezoneSelect } from "@/components/base/TimezoneSelect";
import { detectBrowserTimezone, formatTimezoneLabel, resolveInitialTimezone } from "@/lib/timezone";
import { setActiveCurrency } from "@/lib/currency";
import { friendlyError } from "@/lib/errors";
import { mergeOrgBusinessSettings, parseOrgBusinessIdentity } from "@/lib/org-business-identity";

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

export default function OrganizationSettings() {
  const { org, role, refresh } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [form, setForm] = useState({ name: "", industry: "", currency: "USD", timezone: "UTC", legalName: "", abn: "", address: "", email: "", phone: "", logoUrl: "" });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const detectedZone = useMemo(() => detectBrowserTimezone(), []);

  const { data, isLoading } = useQuery({
    queryKey: ["organization", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from("organizations")
        .select("name, industry, currency, timezone, settings")
        .eq("id", org!.id)
        .maybeSingle();
      return data;
    },
  });

  useEffect(() => {
    if (data) {
      const business = parseOrgBusinessIdentity(data.name, data.settings);
      setForm({
        name: data.name ?? "",
        industry: data.industry ?? "",
        currency: data.currency ?? "USD",
        timezone: resolveInitialTimezone(data.timezone),
        legalName: business.legalName && business.legalName !== (data.name ?? "") ? business.legalName : "",
        abn: business.abn ?? "",
        address: business.address ?? "",
        email: business.email ?? "",
        phone: business.phone ?? "",
        logoUrl: business.logoUrl ?? "",
      });
      setDirty(false);
    }
  }, [data]);

  const updateField = (patch: Partial<typeof form>) => {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
    setMessage("");
    setError("");
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setMessage("");
    setError("");
    setSaving(true);
    const { data: current } = await supabase.from("organizations").select("settings").eq("id", org!.id).maybeSingle();
    const settings = mergeOrgBusinessSettings((current?.settings as Record<string, unknown> | null) ?? {}, {
      legalName: form.legalName.trim() || form.name,
      abn: form.abn.trim() || null,
      address: form.address.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      logoUrl: form.logoUrl.trim() || null,
    });
    const { error: saveError } = await supabase
      .from("organizations")
      .update({ name: form.name, industry: form.industry || null, currency: form.currency, timezone: form.timezone, settings })
      .eq("id", org!.id);
    setSaving(false);
    if (saveError) {
      setError(friendlyError(saveError.message));
      return;
    }
    setDirty(false);
    setMessage("Organization details saved.");
    setActiveCurrency(form.currency);
    queryClient.invalidateQueries({ queryKey: ["organization", org?.id] });
  };

  const handleDeleteOrg = async () => {
    setDeleteError("");
    setDeleting(true);
    const { error } = await supabase.rpc("delete_organization", {
      p_org_id: org!.id,
      p_confirmation: deleteText.trim(),
    });
    if (error) {
      setDeleting(false);
      setDeleteError(friendlyError(error.message));
      return;
    }
    await refresh();
    navigate("/");
  };

  return (
    <AdminOnly title="Organization Settings">
    <div>
      <PageHeader title="Organization Settings" subtitle="Manage your business identity and preferences" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {isLoading ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8"><LoadingState /></div>
          ) : (
            <form onSubmit={handleSave} className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
              <h3 className="text-sm font-semibold text-slate-800">Organization Details</h3>
              {message && <div className="px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">{message}</div>}
              {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className={labelCls}>Organization Name</label>
                  <input required value={form.name} onChange={(e) => updateField({ name: e.target.value })} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Legal / trading name (invoices)</label>
                  <input value={form.legalName} onChange={(e) => updateField({ legalName: e.target.value })} className={inputCls} placeholder={form.name || "Trading name"} />
                </div>
                <div>
                  <label className={labelCls}>ABN</label>
                  <input value={form.abn} onChange={(e) => updateField({ abn: e.target.value })} className={inputCls} placeholder="00 000 000 000" />
                </div>
                <div>
                  <label className={labelCls}>Business email</label>
                  <input value={form.email} onChange={(e) => updateField({ email: e.target.value })} className={inputCls} />
                </div>
              <div>
                <label className={labelCls}>Business phone</label>
                <input value={form.phone} onChange={(e) => updateField({ phone: e.target.value })} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Document logo URL</label>
                <input value={form.logoUrl} onChange={(e) => updateField({ logoUrl: e.target.value })} className={inputCls} placeholder="https://…" />
                <p className="text-xs text-slate-400 mt-1">Used on pick slips, packing slips and tax invoices. Leave blank to print the trading name.</p>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Business address</label>
                  <input value={form.address} onChange={(e) => updateField({ address: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Industry</label>
                  <input value={form.industry} onChange={(e) => updateField({ industry: e.target.value })} className={inputCls} placeholder="Electronics" />
                </div>
                <div>
                  <label className={labelCls}>Default Currency</label>
                  <select value={form.currency} onChange={(e) => updateField({ currency: e.target.value })} className={inputCls}>
                    <option value="AUD">AUD (A$)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="CAD">CAD (C$)</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Timezone</label>
                  <TimezoneSelect
                    value={form.timezone}
                    onChange={(zone) => updateField({ timezone: zone })}
                  />
                  {detectedZone && detectedZone !== form.timezone && (
                    <button
                      type="button"
                      onClick={() => updateField({ timezone: detectedZone })}
                      className="mt-1.5 text-xs text-emerald-600 hover:text-emerald-700 hover:underline whitespace-nowrap"
                    >
                      Use device timezone: {formatTimezoneLabel(detectedZone)}
                    </button>
                  )}
                </div>
              </div>
              <div className="flex justify-end items-center gap-3">
                {dirty && (
                  <span className="flex items-center text-sm text-amber-600 gap-1.5">
                    <i className="ri-error-warning-line" /> Unsaved changes
                  </span>
                )}
                <button type="submit" disabled={saving || !dirty} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg whitespace-nowrap">
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          )}

          {role === "owner" && (
            <div className="bg-white rounded-xl border border-red-200 p-6">
              <h3 className="text-sm font-semibold text-red-700">Danger Zone</h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Permanently delete this organization and all of its data — products, orders, inventory,
                connectors, and members. This cannot be undone.
              </p>
              <button
                onClick={() => { setDeleteError(""); setDeleteText(""); setDeleteOpen(true); }}
                className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg whitespace-nowrap"
              >
                Delete organization
              </button>
            </div>
          )}
        </div>
        <SettingsNav />
      </div>

      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete organization"
        footer={
          <>
            <button
              onClick={() => setDeleteOpen(false)}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteOrg}
              disabled={deleting || deleteText.trim() !== (org?.name ?? "")}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {deleting ? "Deleting..." : "Delete organization"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {deleteError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{deleteError}</div>}
          <p className="text-sm text-slate-600 leading-relaxed">
            This permanently deletes <strong>{org?.name}</strong> and all products, orders, inventory,
            connectors, and members. This action cannot be undone.
          </p>
          <div>
            <label className={labelCls}>
              Type <span className="font-mono">{org?.name}</span> to confirm
            </label>
            <input
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              className={inputCls}
              autoComplete="off"
            />
          </div>
        </div>
      </Modal>
    </div>
    </AdminOnly>
  );
}